import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Textarea } from '../../../../components/inputs/Textarea';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { surveyRespondentsApi } from '../surveyApi';
import { sampleAssessmentRemarks } from '../utils/sampleDevelopmentPlan';

const ID = 'survey-bulk-assess';

/** One response to assess, as the form names it. */
export interface BulkAssessTarget {
  id: string;
  name: string;
  employeeNumber: string;
}

/**
 * Responses › Assessment — assessing SEVERAL ticked employees at once (product call, 13 Oct 2026).
 * ONE Remarks, written once and recorded on each of them; the table says who. Assessing is
 * `surveys.assess`, and each response is still assessed ONCE and for good.
 *
 * The responses are assessed ONE AT A TIME, in the order listed — each is its own record in the
 * trail. If one is refused, those before it stay assessed and LEAVE the table; the refused one and
 * the rest stay, with the server's reason, so a second Submit assesses nobody twice.
 */
export function SurveyBulkAssessModal({
  open,
  surveyId,
  targets,
  skipped,
  onClose,
  onAssessed,
}: {
  open: boolean;
  surveyId: string;
  /** The ticked employees who CAN be assessed. */
  targets: BulkAssessTarget[];
  /** How many ticked employees were left out — assessed already, or above the reader's access level. */
  skipped: number;
  onClose: () => void;
  /** After at least one was assessed — also when a later one was refused. */
  onAssessed: () => void;
}) {
  const toast = useToast();

  // Seeded once: the parent mounts this only while it is open.
  const [remaining, setRemaining] = useState(targets);
  const [remarks, setRemarks] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = remaining.length > 0 && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    let done = 0;

    try {
      for (const target of remaining) {
        await surveyRespondentsApi.assess(surveyId, target.id, remarks.trim() || null);
        done++;
      }

      toast.success(`Assessed ${done} ${done === 1 ? 'response' : 'responses'}.`);
      onAssessed();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The response could not be assessed.', 0));

      if (done > 0) {
        setRemaining((current) => current.slice(done));
        setConfirmed(false);
        toast.success(`Assessed ${done} ${done === 1 ? 'response' : 'responses'} before the one refused.`);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Assess selected responses"
      titleCount={remaining.length}
      size="lg"
      id={`${ID}-modal`}
      autogenerate={{ onGenerate: () => setRemarks(sampleAssessmentRemarks()), disabled: saving }}
      confirm={{ id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${ID}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/* The consequence, before the tick: an assessment is made once and is not undone here. */}
        <p className="text-xs text-slate-600">
          Each response listed is marked assessed with the remarks below. An assessment is made once.
          {skipped > 0
            ? ` ${skipped} selected ${skipped === 1 ? 'employee is' : 'employees are'} left out — assessed already, or above your access level.`
            : null}
        </p>

        <div className="modern-scrollbar max-h-56 overflow-y-auto overflow-x-hidden rounded-lg border border-slate-200">
          <table className="w-full table-auto">
            <thead className="sticky top-0 bg-slate-100">
              <tr className="text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
                <th className="w-12 px-3 py-2 text-left">#</th>
                <th className="border-l border-slate-200 px-3 py-2 text-left">Employee</th>
                <th className="w-44 border-l border-slate-200 px-3 py-2 text-left">Employee No.</th>
              </tr>
            </thead>
            <tbody>
              {remaining.map((target, index) => (
                <tr key={target.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 align-middle font-mono text-sm text-slate-500">{index + 1}</td>
                  <td className="border-l border-slate-100 px-3 py-2 align-middle text-sm text-slate-600">{target.name}</td>
                  <td className="border-l border-slate-100 px-3 py-2 align-middle font-mono text-sm text-slate-500">
                    {target.employeeNumber}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Textarea
          id={`${ID}-remarks`}
          label="Remarks"
          description="Recorded on every response listed. Optional."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          maxLength={2000}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
